import { h, Component } from 'preact';
import { observer } from 'mobx-preact';
import { Text } from 'preact-i18n';
import util from 'preact-util';
import { route } from 'preact-router';

import Avatar from '../gui/avatar';

// Public user profile drawer. Opened via openUserProfileDrawer() in
// lib/userProfile.js from every place a user is displayed. Receives seed
// props (userId, name, image, faceBox) from the click site so the header
// renders instantly, then loads:
//   - GET /api/users/public/:id        (strict public allowlist, 404 = hidden)
//   - GET /api/articles/public/?author (the user's public feed posts/photos)
@observer
class PublicUserProfile extends Component {
    constructor(props) {
        super(props);
        this.state = {
            profile: null,
            articles: [],
            loading: true,
        };
    }

    componentDidMount() {
        this.loadAll();
    }

    loadAll = async () => {
        const { userId } = this.props;
        const { articleStore } = this.props.stores;
        const hostname = articleStore.currentHostname || window.location.hostname;

        const [profileResponse, articlesResponse] = await Promise.all([
            util.fetchApi(`/api/users/public/${userId}`, { method: 'GET' }, {}),
            util.fetchApi('/api/articles/public/', { method: 'GET' }, {
                hostname,
                category: 'feed',
                author: userId,
                limit: 100,
                offset: 0,
                sort: '-sort -createdDate',
            }),
        ]);

        this.setState({
            profile: profileResponse?.status === 200 ? profileResponse.data : null,
            articles: articlesResponse?.status === 200 ? (articlesResponse.data || []) : [],
            loading: false,
        });
    }

    openPost = (e) => {
        const { uuidv4 } = e.target.closest('button').dataset;
        if (!uuidv4) {
            return;
        }
        const { appState } = this.props.stores;
        appState.closeDrawer();
        route(`/feed/${uuidv4}`);
    }

    // Dead-simple profile photo uploader — same drawer as the shooterDetail
    // shortcut. Reloads the profile afterwards so the new photo shows at once.
    openUploadImage = () => {
        const { appState } = this.props.stores;
        const { drawerLevel = 1 } = this.props;
        const { drawerHeightMedium } = appState;
        const { profile } = this.state;
        const targetId = profile?.id || parseInt(this.props.userId, 10);
        if (!targetId) return;
        appState.openDrawer('uploadUserImage', {
            userId: targetId,
            name: profile?.name || this.props.name || '',
            height: drawerHeightMedium,
            callback: () => this.loadAll(),
        }, drawerLevel + 1);
    }

    // Opens the inbox right-drawer deep-linked into the conversation with
    // this user (inbox/drawer.js enters the conversation from its uuidv4
    // prop). Sending the first message there creates the conversation.
    sendMessage = () => {
        const { appState } = this.props.stores;
        const { drawerLevel = 1 } = this.props;
        const { profile } = this.state;
        if (!profile?.uuidv4) {
            return;
        }
        appState.openDrawerRight('inbox', { uuidv4: profile.uuidv4 }, drawerLevel + 1);
    }

    socialLinks = (profile) => {
        const links = [
            { href: profile.url, icon: 'fa-solid fa-globe', prefix: '' },
            { href: profile.facebook, icon: 'fa-brands fa-facebook', prefix: 'https://facebook.com/' },
            { href: profile.instagram, icon: 'fa-brands fa-instagram', prefix: 'https://instagram.com/' },
        ];
        return links
            .filter(link => link.href)
            .map(link => ({
                ...link,
                href: /^https?:\/\//.test(link.href) ? link.href : `${link.prefix || 'https://'}${link.href}`,
            }));
    }

    render() {
        const { name: seedName, image: seedImage, faceBox: seedFaceBox } = this.props;
        const { profile, articles, loading } = this.state;
        const { userStore } = this.props.stores;
        const { user: viewer = {}, isAdmin } = userStore;
        // Messaging needs a logged-in viewer and the loaded profile's uuidv4
        // (the messaging address) — and no button on your own profile.
        const canSendMessage = viewer.id > 0 && !!profile?.uuidv4 && viewer.id !== profile.id;
        // Photo upload: yourself, or site admin — same rule as shooterDetail.
        const targetId = profile?.id || parseInt(this.props.userId, 10) || 0;
        const canUploadImage = targetId > 0 && ((viewer.id > 0 && viewer.id === targetId) || isAdmin);

        const name = profile?.name || seedName || '';
        const image = profile?.image || seedImage;
        const faceBox = profile?.imageFaceBox || (profile?.image ? null : seedFaceBox);
        const clubLine = [profile?.sportsClub, profile?.kennel].filter(Boolean).join(' · ');
        const memberSinceYear = profile?.createdDate ? new Date(profile.createdDate).getFullYear() : null;
        const socialLinks = profile ? this.socialLinks(profile) : [];
        const photos = articles
            .flatMap(article => (article.images || []).map(img => ({ article, img })))
            .filter(entry => entry.img?.s3SmallLink || entry.img?.s3MediumLink);

        return (
            <div class='px-3 pb-4' style='overflow-x: hidden;'>
                {/* Profile header */}
                <div class='d-flex flex-column align-items-center text-center pt-2'>
                    <div style='position: relative;'>
                        <Avatar name={name} image={image} faceBox={faceBox} size={96} online={profile?.online} />
                        {/* Camera badge bottom-LEFT — the online dot owns bottom-right. */}
                        {canUploadImage && (
                            <button
                                type='button'
                                class='btn btn-primary rounded-circle p-0 d-flex align-items-center justify-content-center position-absolute shadow-sm'
                                style='width: 30px; height: 30px; bottom: 0; left: -6px; font-size: 14px;'
                                onClick={this.openUploadImage}
                                title='Last opp profilbilde'
                            >
                                <i class='fa-solid fa-camera' />
                            </button>
                        )}
                    </div>
                    {canUploadImage && !image && (
                        <button
                            type='button'
                            class='btn btn-sm btn-primary rounded-pill mt-2'
                            onClick={this.openUploadImage}
                        >
                            <i class='fa-solid fa-camera me-1' />
                            <Text id='user.upload-image-title'>Upload profile photo</Text>
                        </button>
                    )}
                    <h4 class='mt-3 mb-0 text-body' style='word-break: break-word;'>{name}</h4>
                    {clubLine && (
                        <div class='text-body-secondary small mt-1'>{clubLine}</div>
                    )}
                    {memberSinceYear > 0 && (
                        <div class='text-body-secondary small mt-1'>
                            <i class='fa-regular fa-calendar me-1' />
                            <Text id='userProfile.member-since' fields={{ year: memberSinceYear }}>Member since {memberSinceYear}</Text>
                        </div>
                    )}
                    {profile?.description && (
                        <p class='text-body mt-3 mb-0' style='white-space: pre-wrap; word-break: break-word; max-width: 480px;'>
                            {profile.description}
                        </p>
                    )}
                    {socialLinks.length > 0 && (
                        <div class='d-flex flex-row justify-content-center gap-4 mt-3'>
                            {socialLinks.map(link => (
                                <a
                                    key={link.icon}
                                    href={link.href}
                                    target='_blank'
                                    rel='noopener noreferrer'
                                    class='text-body-secondary'
                                    style='font-size: 1.3rem;'
                                >
                                    <i class={link.icon} />
                                </a>
                            ))}
                        </div>
                    )}
                    {canSendMessage && (
                        <button
                            type='button'
                            class='btn btn-primary mt-3'
                            onClick={this.sendMessage}
                        >
                            <i class='fa-duotone fa-paper-plane me-2' />
                            <Text id='userProfile.send-message'>Send message</Text>
                        </button>
                    )}
                </div>

                {/* Stats */}
                {!loading && (
                    <div class='d-flex flex-row justify-content-center gap-4 mt-3 text-center'>
                        <div>
                            <div class='fw-bold text-body'>{articles.length}</div>
                            <small class='text-body-secondary'><Text id='userProfile.posts'>Posts</Text></small>
                        </div>
                        <div>
                            <div class='fw-bold text-body'>{photos.length}</div>
                            <small class='text-body-secondary'><Text id='userProfile.photos'>Photos</Text></small>
                        </div>
                    </div>
                )}

                <hr class='my-3' />

                {/* Posted photos */}
                {loading ? (
                    <div class='text-center py-5'>
                        <div class='spinner-border text-body-secondary' role='status' />
                    </div>
                ) : photos.length > 0 ? (
                    <div style='display: grid; grid-template-columns: repeat(3, 1fr); gap: 4px;'>
                        {photos.map((entry, idx) => (
                            <button
                                key={`${entry.article.uuidv4}-${idx}`}
                                type='button'
                                class='p-0 border-0 bg-transparent'
                                style='aspect-ratio: 1 / 1; overflow: hidden;'
                                data-uuidv4={entry.article.uuidv4}
                                onClick={this.openPost}
                            >
                                <img
                                    src={entry.img.s3SmallLink || entry.img.s3MediumLink}
                                    class='w-100 h-100 rounded-3'
                                    style='object-fit: cover;'
                                    loading='lazy'
                                    alt=''
                                />
                            </button>
                        ))}
                    </div>
                ) : (
                    <div class='text-center py-4 text-body-secondary'>
                        <i class='fa-regular fa-image fa-2x mb-2' />
                        <div><Text id='userProfile.no-posts'>No public posts yet.</Text></div>
                    </div>
                )}
            </div>
        );
    }
}

export default PublicUserProfile;
